// NOTE: script-editor-only client for POST /api/i/scripts/sql/preview — useSqlRunner remains the reporting endpoint client (spec §9).
import {useCallback, useMemo, useState} from 'react'
import {useMutation} from '@tanstack/react-query'
import {useServices} from '@/services/context'
import {ApiError} from '@/api/client'
import type {SqlPreviewRequest, SqlPreviewResponse} from '@/types/sql-preview'

export interface UseSqlPreviewResult {
    isRunning: boolean
    result: SqlPreviewResponse | null
    error: Error | null
    run: (req: SqlPreviewRequest) => Promise<SqlPreviewResponse>
    reset: () => void
}

const MAX_RETRY_ATTEMPTS = 2

/**
 * Mutation hook for the always-rollback SQL preview endpoint
 * ({@code POST /api/i/scripts/sql/preview}). Used by the v2 script SQL dialog.
 *
 * Throws shaped errors so the dialog's banner mapping can route on the right
 * variant: `error.errorCode`, `error.limit`, `error.retryAfterSeconds`,
 * `error.httpStatus`, `error.isNetworkFailure`.
 */
export function useSqlPreview(): UseSqlPreviewResult {
    const services = useServices()
    const [result, setResult] = useState<SqlPreviewResponse | null>(null)
    const [error, setError] = useState<Error | null>(null)

    const [isRunning, setIsRunning] = useState(false)
    const {mutateAsync, reset: mutationReset} = useMutation<SqlPreviewResponse, Error, SqlPreviewRequest>({
        mutationFn: async (req) => {
            try {
                return await services.scripts.sqlPreview(buildWireBody(req))
            } catch (cause) {
                throw toShapedError(cause)
            }
        },
        retry: (failureCount, err) => {
            if (failureCount >= MAX_RETRY_ATTEMPTS) return false
            return isRetryable(err)
        },
    })

    const run = useCallback(
        async (req: SqlPreviewRequest): Promise<SqlPreviewResponse> => {
            // WHY: drive isRunning ourselves (rather than reading mutation.isPending)
            // so the synchronous render after fireEvent.click flips Try-SQL to
            // disabled — useMutation's isPending is one microtask late.
            setIsRunning(true)
            setError(null)
            setResult(null)
            try {
                const r = await mutateAsync(req)
                setResult(r)
                return r
            } catch (e) {
                const err = e instanceof Error ? e : new Error(String(e))
                setError(err)
                throw err
            } finally {
                setIsRunning(false)
            }
        },
        [mutateAsync],
    )

    const reset = useCallback(() => {
        setResult(null)
        setError(null)
        setIsRunning(false)
        mutationReset()
    }, [mutationReset])

    return useMemo(
        () => ({isRunning, result, error, run, reset}),
        [isRunning, result, error, run, reset],
    )
}

function buildWireBody(req: SqlPreviewRequest): SqlPreviewRequest {
    const body: SqlPreviewRequest = {
        sql: req.sql,
        binding: req.binding,
    }
    if (req.paramsLiteral !== undefined && req.paramsLiteral !== null && req.paramsLiteral !== '') {
        body.paramsLiteral = req.paramsLiteral
    }
    // WHY: backend ignores valueType outside queryForValue; the contract for this
    // hook is "only include it when the binding actually consumes it" so wire
    // traffic and the request log match what the user picked.
    if (req.binding === 'queryForValue' && req.valueType) {
        body.valueType = req.valueType
    }
    return body
}

interface ShapedErrorOpts {
    errorCode?: string
    message?: string
    limit?: string
    retryAfterSeconds?: number
    httpStatus?: number
    isNetworkFailure?: boolean
}

function toShapedError(cause: unknown): Error {
    if (cause instanceof ApiError) {
        const body = parseErrorBody(cause.errorDetail)
        const opts: ShapedErrorOpts = {
            httpStatus: cause.status,
            message: body?.message ?? cause.message,
        }
        if (cause.status === 0) {
            opts.isNetworkFailure = true
        }
        if (typeof body?.errorCode === 'string') opts.errorCode = body.errorCode
        if (typeof body?.limit === 'string') opts.limit = body.limit
        if (typeof body?.retryAfterSeconds === 'number') opts.retryAfterSeconds = body.retryAfterSeconds
        return makeShapedError(opts)
    }
    if (cause instanceof Error) {
        // WHY: callers / tests may throw an already-shaped Error (errorCode / limit /
        // retryAfterSeconds / httpStatus / isNetworkFailure attached). Pass it through
        // unchanged so the dialog's deriveBannerDescriptor can route on the existing
        // fields. Only fall back to "network failure" when there is no shape.
        const e = cause as Error & {
            errorCode?: unknown
            limit?: unknown
            retryAfterSeconds?: unknown
            httpStatus?: unknown
            status?: unknown
            isNetworkFailure?: unknown
        }
        const hasShape =
            typeof e.errorCode === 'string'
            || typeof e.limit === 'string'
            || typeof e.retryAfterSeconds === 'number'
            || typeof e.httpStatus === 'number'
            || typeof e.status === 'number'
            || e.isNetworkFailure === true
        if (hasShape) {
            return cause
        }
        return makeShapedError({message: cause.message, isNetworkFailure: true})
    }
    return makeShapedError({message: String(cause), isNetworkFailure: true})
}

function parseErrorBody(raw: string | undefined): {
    errorCode?: string
    message?: string
    limit?: string
    retryAfterSeconds?: number
} | null {
    if (!raw) return null
    try {
        const parsed = JSON.parse(raw) as Record<string, unknown>
        return {
            errorCode: typeof parsed.errorCode === 'string' ? parsed.errorCode : undefined,
            message: typeof parsed.message === 'string' ? parsed.message : undefined,
            limit: typeof parsed.limit === 'string' ? parsed.limit : undefined,
            retryAfterSeconds: typeof parsed.retryAfterSeconds === 'number' ? parsed.retryAfterSeconds : undefined,
        }
    } catch (_jsonParseError) {
        // WHY: errorDetail isn't guaranteed JSON (raw text from the server is a
        // legitimate shape). Silently fall back to "no parsed body" — the caller
        // routes on httpStatus and message which are always populated by ApiError.
        return null
    }
}

// NOTE: keys are hardcoded — never spread untrusted JSON into err.
function makeShapedError(opts: ShapedErrorOpts): Error {
    const err = new Error(opts.message ?? 'SQL preview failed')
    if (opts.errorCode !== undefined) Object.assign(err, {errorCode: opts.errorCode})
    if (opts.limit !== undefined) Object.assign(err, {limit: opts.limit})
    if (opts.retryAfterSeconds !== undefined) Object.assign(err, {retryAfterSeconds: opts.retryAfterSeconds})
    if (opts.httpStatus !== undefined) Object.assign(err, {httpStatus: opts.httpStatus})
    if (opts.isNetworkFailure) Object.assign(err, {isNetworkFailure: true})
    return err
}

function isRetryable(err: Error): boolean {
    const e = err as Error & {httpStatus?: number; isNetworkFailure?: boolean}
    if (e.isNetworkFailure) return true
    if (typeof e.httpStatus === 'number' && e.httpStatus >= 500) return true
    return false
}
